import React from "react";
import {
    buildYoutubeEmbedUrl,
    getEmbedUrlFromPornHubUrl,
    getEmbedUrlFromRedGifsUrl,
    safeIframeEmbedSrc,
} from "@seavien/commons";
import { ImageOff, Play } from "lucide-react";
import {
    type DiscussionCardMediaItem,
    parseDiscussionCardMediaItems,
} from "@/main/react/card/DiscussionCardMedia";

import SafeImage from "@/main/util/SafeImage";
import { cn } from "@/lib/utils";
import {
    Carousel,
    type CarouselApi,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "@/components/ui/carousel";
import type { ImageCarouselRenderabilityState } from "@/main/react/image/ImageCarouselDisplayer";
import type { ImageSizeLike } from "@/main/util/ImageUrlHelper";
import type { Localizer } from "@/main/react/Types";
import { recordMediaOpen } from "@/main/react/discussion/MediaAttentionSession";

export type { DiscussionCardMediaItem } from "@/main/react/card/DiscussionCardMedia";
export { parseDiscussionCardMediaItems } from "@/main/react/card/DiscussionCardMedia";

type Props = {
    items: readonly DiscussionCardMediaItem[];
    alt?: string;
    onTap?: () => void;
    onActiveIndexChange?: (index: number) => void;
    /**
     * The post whose media this carousel shows, for the P3 `opens` primitive: a "Play here" on a gated
     * embed is credited to that post's page-instance media-attention session
     * (`MediaAttentionSession.recordMediaOpen`; round-5 slice `MD-2b`). Credited only when the page
     * instance IS that post, so a related-discussion card on a discussion page never counts against
     * the post being read, and a feed card (no page instance) credits nothing. Omit on surfaces that
     * do not name a post.
     */
    mediaAttentionDiscussionPublicId?: string | null;
    localizer: Localizer;
    className?: string;
    slideHeightClassName?: string;
    onRenderabilityChange?: (state: ImageCarouselRenderabilityState) => void;
    /**
     * Reports whether this carousel actually PAGES — i.e. whether it rendered the arrows and dots,
     * which are both gated on `preparedItems.length > 1`.
     *
     * It exists so a caller can know that WITHOUT recomputing it. The count that matters is
     * `preparedItems`, taken after unrenderable slides are dropped, and only this component has it;
     * a caller counting its own `items` would be the second rule that disagrees, which is exactly
     * what `CardMediaPagingContext` above refuses to introduce.
     *
     * `DiscussionCardDisplayer` uses it to decide whether the media block outranks the card's own
     * full-card link. A carousel that does not page needs no pointer, and taking the pointer anyway
     * is what made the card stop being a link over its image (rule 26; see
     * `designs/01-now/2026-09-01-discussion-card-media-swallows-the-card-link-design.md`).
     */
    onPagingChange?: (paging: boolean) => void;
    seavienImageSize?: ImageSizeLike | null;
    /**
     * Candidate sizes for a responsive `srcset` on the foreground card image (CWV N3). Forwarded to
     * SafeImage so mobile/low-DPR viewports can download a smaller variant than `seavienImageSize`.
     */
    seavienSrcSetSizes?: readonly ImageSizeLike[] | null;
    /** `sizes` attribute paired with `seavienSrcSetSizes`. */
    imageSizes?: string;
    /**
     * When true, the first image slide's foreground image is rendered as a high-priority,
     * eager LCP candidate (see SafeImage `priority`). Set only for the first above-the-fold
     * home-feed card. See 2026-06-11-homepage-cwv-remaining-structural-slices-design.md (Slice A).
     */
    priorityFirstSlide?: boolean;
};

/**
 * Whether the SURFACE rendering a feed card is willing to let the card's own media carousel page
 * horizontally (`LANDING-15`).
 *
 * The marketing showcase on `/start-a-series` puts these cards inside a carousel of its own, under a
 * feature carousel, so a visitor sees two rails at once and each card draws a third: identical
 * chevrons over the photo and an identical dot rail beneath it, with nothing indicating which rail a
 * control belongs to. On a phone it is a gesture conflict rather than a cosmetic one — a horizontal
 * swipe starting on a card image pages that card's photos, and the same swipe a few pixels lower
 * pages the showcase. A visitor trying to see the next series pages the photos of the one they are
 * already looking at, with no feedback that they operated a different control.
 *
 * `"cover-only"` prepares the first item and nothing else, which is how the suppression reaches the
 * arrows and the dots: both are already gated on `preparedItems.length > 1`, so this reuses the
 * component's own rule instead of adding a second one that could disagree with it. The card keeps its
 * cover, which it has always supported.
 *
 * A context and not a prop because of who renders what: the showcase renders `DiscussionCardDisplayer`,
 * and the displayer renders this carousel. Threading a prop through would put the decision in the
 * card, which is shared with the live feed, where paging is correct. Defaults to `"paging"`, so every
 * surface that has not opted in is unchanged.
 */
export const CardMediaPagingContext = React.createContext<{ mode: "paging" | "cover-only" }>({
    mode: "paging",
});

type SlideStatus = "idle" | "loaded" | "error";

type PreparedDiscussionCardMediaItem = DiscussionCardMediaItem & {
    previewUrl: string | null;
    embedUrl: string | null;
};

/**
 * The status a slide starts in, computed in ONE place because three call sites need it and a
 * disagreement between them is invisible.
 *
 * The click-to-activate kinds start "loaded": their slide paints its poster inside the activation
 * button rather than through the `SafeImage` path that reports load/error, so leaving them "idle"
 * would let the broken-slide fallback claim a perfectly healthy clip or embed.
 */
function initialSlideStatus(item: DiscussionCardMediaItem): SlideStatus {
    return item.kind === "iframe" || item.kind === "clip" ? "loaded" : "idle";
}

/**
 * The renderability report, derived from the slide statuses. Pulled out of the component as a pure
 * function so the SAME derivation can run in two places that must never disagree: the memo that feeds
 * the reporting effect, and `setSlideStatus`, which reports synchronously from inside the load/error
 * event so the parent's write batches with this component's.
 */
function deriveRenderabilityState(
    totalImages: number,
    resolvedSlideStatuses: readonly SlideStatus[],
    activeIndex: number,
): ImageCarouselRenderabilityState {
    const loadedImages = resolvedSlideStatuses.filter((status) => status === "loaded").length;
    const failedImages = resolvedSlideStatuses.filter((status) => status === "error").length;
    const hasRenderableImages = loadedImages > 0;
    const allImagesFailed = totalImages > 0 && failedImages === totalImages;

    /**
     * `isResolving` is a question about the ACTIVE slide, not about every slide, and that
     * distinction is the whole fix.
     *
     * <p>It used to read `!hasRenderableImages && !allImagesFailed`, which silently assumed every
     * slide eventually reports. It does not: slides past the visible one are `loading="lazy"` and
     * clipped by the carousel viewport, so the browser never starts their fetch and they fire
     * neither `load` nor `error` — they stay `"idle"` forever. One such slide makes
     * `failedImages === totalImages` unreachable, so a card whose images are all dead sat behind
     * the loading overlay for the life of the mount instead of saying "Image unavailable". The
     * auto-advance below hops off a broken slide 0 exactly once (it is guarded on
     * `activeIndex !== 0`), so THREE dead slides were enough. Prod post `ZzZyP92uWxJi` had 13.
     *
     * <p>Keying on the active slide is also the honest reading of what the overlay is for: it
     * covers the moment before the card's own cover paints. Once that slide has a verdict the card
     * knows what to render — the picture, or the broken-image card for the slide sitting under the
     * reader's eyes — and a slide three positions away that may never be looked at has no bearing
     * on it.
     */
    const activeSlideStatus = resolvedSlideStatuses[activeIndex] ?? "idle";

    return {
        totalImages,
        loadedImages,
        failedImages,
        hasRenderableImages,
        allImagesFailed,
        isResolving: totalImages > 0 && !hasRenderableImages && activeSlideStatus === "idle",
    };
}

/**
 * Content equality for the renderability report.
 *
 * <p>AGENTS.md rule 31: a helper that always constructs its result must return the object it was given
 * when the CONTENT is identical. The consumer of this value is a `useState` setter in the card
 * (`setImageRenderability`), and `useState` bails out on `Object.is` — so a freshly built but
 * content-identical report re-renders the whole card, and through it the whole island.
 */
function areRenderabilityStatesEqual(
    left: ImageCarouselRenderabilityState | null,
    right: ImageCarouselRenderabilityState,
): boolean {
    return left != null
        && left.totalImages === right.totalImages
        && left.loadedImages === right.loadedImages
        && left.failedImages === right.failedImages
        && left.hasRenderableImages === right.hasRenderableImages
        && left.allImagesFailed === right.allImagesFailed
        && left.isResolving === right.isResolving;
}

function normalizeMediaUrl(value: unknown): string | null {
    if (typeof value !== "string") {
        return null;
    }

    const trimmed = value.trim();
    return trimmed ? trimmed : null;
}

function buildEmbedUrl(item: DiscussionCardMediaItem): string | null {
    if (item.kind !== "iframe") {
        return null;
    }

    // A URL that is not a recognized provider embed URL must never become an iframe `src`.
    // The provider helpers return null on no-match; safeIframeEmbedSrc additionally enforces the
    // https-only scheme at the sink (mirrors commons renderHTML). "" → null drops the slide, which
    // the carousel already handles like a failed image (findNextUsableSlide / BrokenImageCard).
    switch (item.provider) {
        case "youtube":
            return (
                safeIframeEmbedSrc(
                    buildYoutubeEmbedUrl({
                        url: item.sourceUrl,
                        autoplay: true,
                        controls: true,
                        modestBranding: true,
                        nocookie: true,
                    }),
                ) || null
            );
        case "redGifs":
            return safeIframeEmbedSrc(getEmbedUrlFromRedGifsUrl(item.sourceUrl)) || null;
        case "pornHub":
            return safeIframeEmbedSrc(getEmbedUrlFromPornHubUrl(item.sourceUrl)) || null;
        default:
            return null;
    }
}

function providerDisplayName(provider?: DiscussionCardMediaItem["provider"]): string {
    switch (provider) {
        case "youtube":
            return "YouTube";
        case "redGifs":
            return "RedGifs";
        case "pornHub":
            return "Pornhub";
        case "linkCard":
            return "Link";
        default:
            return "Video";
    }
}

function findNextUsableSlide(statuses: readonly SlideStatus[], failedIndex: number): number | null {
    for (let index = failedIndex + 1; index < statuses.length; index += 1) {
        if (statuses[index] !== "error") {
            return index;
        }
    }

    return null;
}

function BrokenImageCard({ localizer }: { localizer: Localizer }) {
    return (
        <div className="relative z-[1] flex h-full w-full items-center justify-center p-4 sm:p-6">
            <div className="flex max-w-sm flex-col items-center gap-2 rounded-xl border border-dashed border-border bg-background1/90 px-5 py-6 text-center shadow-sm backdrop-blur-sm">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-background2 text-neutral">
                    <ImageOff className="h-6 w-6" aria-hidden={true} />
                </div>
                <div className="text-sm font-semibold text-text-main">{localizer("discussion.card.media.image_unavailable")}</div>
                <p className="mb-0 text-xs leading-relaxed text-neutral-text">{localizer("discussion.card.media.image_unavailable_help")}</p>
            </div>
        </div>
    );
}

export default function DiscussionCardMediaCarousel({
    items,
    alt = "",
    onTap,
    onActiveIndexChange,
    mediaAttentionDiscussionPublicId = null,
    localizer,
    className,
    slideHeightClassName = "h-100",
    onRenderabilityChange,
    onPagingChange,
    seavienImageSize = null,
    seavienSrcSetSizes = null,
    imageSizes,
    priorityFirstSlide = false,
}: Props) {
    const { mode: mediaPagingMode } = React.useContext(CardMediaPagingContext);
    const preparedItems = React.useMemo<PreparedDiscussionCardMediaItem[]>(() => {
        // `LANDING-15`. Sliced BEFORE anything downstream reads it, so the arrows, the dot rail, the
        // slide-status bookkeeping and the renderability report all describe the same one slide. A
        // suppression applied only to the controls would leave embla with slides it can still drag to.
        const pagedItems = mediaPagingMode === "cover-only" ? items.slice(0, 1) : items;
        return pagedItems.map((item) => ({
            ...item,
            previewUrl: normalizeMediaUrl(item.previewImageUrl ?? (item.kind === "image" ? item.sourceUrl : null)),
            embedUrl: buildEmbedUrl(item),
        }));
    }, [items, mediaPagingMode]);

    const [carouselApi, setCarouselApi] = React.useState<CarouselApi | null>(null);
    const [activeIndex, setActiveIndex] = React.useState(0);
    const [slideStatuses, setSlideStatuses] = React.useState<SlideStatus[]>(() => preparedItems.map(initialSlideStatus));
    const [loadedIframeIndexes, setLoadedIframeIndexes] = React.useState<Set<number>>(() => new Set());
    // Mirror of the state for `markIframeAsLoaded`'s once-per-slide check, readable inside the
    // callback without adding the state to its deps (the same shape as `slideStatusesRef` above).
    const loadedIframeIndexesRef = React.useRef(loadedIframeIndexes);
    const preparedItemsKey = React.useMemo(
        () => preparedItems.map((item) => `${item.kind}|${item.sourceUrl}|${item.previewUrl ?? ""}|${item.embedUrl ?? ""}`).join("\u0001"),
        [preparedItems],
    );
    const previousPreparedItemsKeyRef = React.useRef<string | null>(null);
    const resolvedSlideStatuses = React.useMemo(
        () => preparedItems.map((item, index) => slideStatuses[index] ?? initialSlideStatus(item)),
        [preparedItems, slideStatuses],
    );

    /**
     * The last report handed to the parent, whatever produced it.
     *
     * <p>It is read by BOTH reporters below, and that is what keeps them from double-reporting: the
     * synchronous one stores what it sent, and the memo then returns that same object for as long as
     * the content matches, so the effect's dependency does not change and it stays quiet.
     */
    const lastRenderabilityRef = React.useRef<ImageCarouselRenderabilityState | null>(null);
    const stabilizeRenderability = React.useCallback((next: ImageCarouselRenderabilityState) => {
        if (areRenderabilityStatesEqual(lastRenderabilityRef.current, next)) {
            return lastRenderabilityRef.current as ImageCarouselRenderabilityState;
        }
        lastRenderabilityRef.current = next;
        return next;
    }, []);

    const renderabilityState = React.useMemo<ImageCarouselRenderabilityState>(
        () => stabilizeRenderability(deriveRenderabilityState(preparedItems.length, resolvedSlideStatuses, activeIndex)),
        [activeIndex, preparedItems.length, resolvedSlideStatuses, stabilizeRenderability],
    );

    React.useEffect(() => {
        if (previousPreparedItemsKeyRef.current == null) {
            previousPreparedItemsKeyRef.current = preparedItemsKey;
            return;
        }

        if (previousPreparedItemsKeyRef.current === preparedItemsKey) {
            return;
        }

        previousPreparedItemsKeyRef.current = preparedItemsKey;
        const resetStatuses = preparedItems.map(initialSlideStatus);
        // Keep the mirror in step with the state, or the next load/error handler derives its report
        // from the statuses of a slide set that is no longer on screen.
        slideStatusesRef.current = resetStatuses;
        setSlideStatuses(resetStatuses);
        setActiveIndex(0);
        setLoadedIframeIndexes(new Set());
    }, [preparedItems, preparedItemsKey]);

    React.useEffect(() => {
        if (!carouselApi) return;

        const update = () => {
            try {
                setActiveIndex(carouselApi.selectedScrollSnap());
            } catch {
                setActiveIndex(0);
            }
        };

        update();
        carouselApi.on("select", update);
        carouselApi.on("reInit", update);

        return () => {
            try {
                carouselApi.off("select", update);
                carouselApi.off("reInit", update);
            } catch {
                // ignore
            }
        };
    }, [carouselApi]);

    // Re-init on a change of the SLIDE SET, never on a change of a slide's load status.
    //
    // `slideStatuses` used to be a dependency here, and it is written once per slide that reports
    // `load` or `error`. Each write therefore forced a full embla re-init, which re-emits `reInit`
    // synchronously to every listener: this component's own `select`/`reInit` handler (`setActiveIndex`)
    // and the shadcn `Carousel`'s (`setCanScrollPrev`/`setCanScrollNext`). That is the second of the two
    // island commits every feed card was paying per image, measured on devo as an exact repeating pair
    // (designs/01-now/2026-08-24-island-render-loop-remediation.md §2.3). Twelve cards is twenty-four
    // commits against the diagnostic's threshold of 25 and React's #185 ceiling of 50.
    //
    // Nothing is lost: what an image load actually changes is the slide's measured size, and embla
    // watches that itself (`watchResize`, on by default). The re-init that IS needed is the one where
    // the set of slides changed, and `preparedItemsKey` is the value that says so — the same key the
    // status-reset effect above keys on, so the two cannot drift apart.
    React.useEffect(() => {
        if (!carouselApi?.reInit) return;
        carouselApi.reInit();
    }, [carouselApi, preparedItemsKey]);

    React.useEffect(() => {
        if (!carouselApi) return;
        if (activeIndex !== 0) return;
        if (resolvedSlideStatuses[0] !== "error") return;

        const nextUsableIndex = findNextUsableSlide(resolvedSlideStatuses, 0);
        if (nextUsableIndex == null) return;

        carouselApi.scrollTo(nextUsableIndex);
        setActiveIndex(nextUsableIndex);
    }, [activeIndex, carouselApi, resolvedSlideStatuses]);

    // The callback is read through a ref so a caller passing an inline arrow cannot re-arm the
    // reporting effect on every one of its own renders. The effect below is keyed on the VALUE, which
    // is the only thing the parent needs to hear about.
    const onRenderabilityChangeRef = React.useRef(onRenderabilityChange);
    React.useEffect(() => {
        onRenderabilityChangeRef.current = onRenderabilityChange;
    }, [onRenderabilityChange]);

    /**
     * The single reporting door, and it is idempotent by identity.
     *
     * <p>Both reporters below can be reached for the same value: the load/error handler sends it inside
     * the event, and the effect sends whatever the memo last produced. Without this guard the effect
     * re-sends what the handler already sent, the parent's `useState` is handed a value it already
     * holds, and React still renders that component once before bailing out — which is a whole extra
     * island commit per image, i.e. the defect this was meant to remove.
     */
    const lastReportedRef = React.useRef<ImageCarouselRenderabilityState | null>(null);
    const reportRenderability = React.useCallback((value: ImageCarouselRenderabilityState) => {
        if (lastReportedRef.current === value) {
            return;
        }
        lastReportedRef.current = value;
        onRenderabilityChangeRef.current?.(value);
    }, []);

    /**
     * The paging report. Same shape as the renderability door above and for the same two reasons: the
     * callback is read through a ref so an inline arrow cannot re-arm the effect, and the value is
     * compared before sending so an unchanged report never reaches the parent's `useState`
     * (`AGENTS.md` rule 31 — a no-op write that re-renders the card is the defect this shape avoids).
     *
     * `preparedItems.length > 1` is the SAME expression the arrows and the dot rail are gated on, read
     * from the same memo, so the parent can never disagree with what was actually rendered.
     */
    const pages = preparedItems.length > 1;
    const onPagingChangeRef = React.useRef(onPagingChange);
    React.useEffect(() => {
        onPagingChangeRef.current = onPagingChange;
    }, [onPagingChange]);

    const lastReportedPagingRef = React.useRef<boolean | null>(null);
    React.useEffect(() => {
        if (lastReportedPagingRef.current === pages) {
            return;
        }
        lastReportedPagingRef.current = pages;
        onPagingChangeRef.current?.(pages);
    }, [pages]);

    React.useEffect(() => {
        reportRenderability(renderabilityState);
    }, [renderabilityState, reportRenderability]);

    React.useEffect(() => {
        onActiveIndexChange?.(activeIndex);
    }, [activeIndex, onActiveIndexChange]);

    // Mirrors `slideStatuses` so the load/error handler can derive the NEXT report without waiting for
    // a render. Written in the handler itself rather than in an effect: the whole point is to be ahead
    // of the commit.
    const slideStatusesRef = React.useRef(slideStatuses);
    React.useEffect(() => {
        slideStatusesRef.current = slideStatuses;
    }, [slideStatuses]);
    const activeIndexRef = React.useRef(activeIndex);
    activeIndexRef.current = activeIndex;
    const preparedItemsRef = React.useRef(preparedItems);
    preparedItemsRef.current = preparedItems;

    /**
     * Report the slide statuses upward from INSIDE the load/error event, not from an effect.
     *
     * <p>This is the first of the two island commits every feed card was paying per image, and the
     * reason it is worth the ref bookkeeping. `SafeImage`'s `onLoad` writes its own `loaded` state and
     * calls `setSlideStatus` here; React batches those two into one commit because they happen inside
     * one event handler. The parent's `setImageRenderability` used to arrive one commit LATER, from the
     * effect below, because an effect runs after the commit that scheduled it — so the card, and with
     * it the whole island, committed twice per image. Reporting here puts the parent's write inside the
     * same batch, and the pair collapses to one.
     *
     * <p>The effect is still the reporter for every other path (an `activeIndex` change, the slide-set
     * reset). It does not double-report, because both reporters go through `stabilizeRenderability`:
     * once this call has banked a value, the memo returns that same object while the content matches,
     * so the effect's dependency never changes. Measured by
     * `feedCardImageLoadCommitBudget.integration.test.tsx`.
     */
    const setSlideStatus = React.useCallback((index: number, status: SlideStatus) => {
        const current = slideStatusesRef.current;
        if (current[index] === status) {
            return;
        }

        const next = [...current];
        next[index] = status;
        slideStatusesRef.current = next;
        setSlideStatuses(next);

        const items = preparedItemsRef.current;
        const resolved = items.map((item, itemIndex) => next[itemIndex] ?? initialSlideStatus(item));
        reportRenderability(
            stabilizeRenderability(deriveRenderabilityState(items.length, resolved, activeIndexRef.current)),
        );
    }, [reportRenderability, stabilizeRenderability]);

    const markIframeAsLoaded = React.useCallback((index: number) => {
        if (loadedIframeIndexesRef.current.has(index)) {
            return; // the gate is already open on this slide: not a new play, and no state change
        }
        // P3 `opens` (media-attention design §4; slice `MD-2b`): "Play here" is an intent. Once per
        // slide, credited to the named post's page-instance session; never posted from here.
        recordMediaOpen(mediaAttentionDiscussionPublicId);
        setLoadedIframeIndexes((current) => {
            if (current.has(index)) {
                return current;
            }

            const next = new Set(current);
            next.add(index);
            loadedIframeIndexesRef.current = next;
            return next;
        });
    }, [mediaAttentionDiscussionPublicId]);

    const tapIntentRef = React.useRef<{
        pointerId: number;
        startX: number;
        startY: number;
        moved: boolean;
        ignoreTap: boolean;
    } | null>(null);

    const shouldIgnoreTapTarget = React.useCallback((target: EventTarget | null): boolean => {
        if (!(target instanceof Element)) {
            return false;
        }

        return Boolean(target.closest("button, a, input, select, textarea, summary, iframe, [role='button'], [data-ignore-carousel-tap='true']"));
    }, []);

    const handlePointerDownCapture = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        tapIntentRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            moved: false,
            ignoreTap: shouldIgnoreTapTarget(event.target),
        };
    }, [shouldIgnoreTapTarget]);

    const handlePointerMoveCapture = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        const tapIntent = tapIntentRef.current;
        if (!tapIntent || tapIntent.pointerId !== event.pointerId || tapIntent.moved) {
            return;
        }

        if (Math.abs(event.clientX - tapIntent.startX) > 8 || Math.abs(event.clientY - tapIntent.startY) > 8) {
            tapIntent.moved = true;
        }
    }, []);

    const resetTapIntent = React.useCallback(() => {
        tapIntentRef.current = null;
    }, []);

    const handlePointerUpCapture = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        const tapIntent = tapIntentRef.current;
        if (!tapIntent || tapIntent.pointerId !== event.pointerId) {
            return;
        }

        const shouldTriggerTap = !tapIntent.moved && !tapIntent.ignoreTap && !shouldIgnoreTapTarget(event.target);
        resetTapIntent();

        if (!shouldTriggerTap) {
            return;
        }

        onTap?.();
    }, [onTap, resetTapIntent, shouldIgnoreTapTarget]);

    if (preparedItems.length === 0) {
        return null;
    }

    const nextMediaLabel = localizer("discussion.card.media.next");
    const previousMediaLabel = localizer("discussion.card.media.previous");

    return (
        <div
            className={cn("relative mx-auto w-full", onTap && "cursor-pointer", className)}
            onPointerDownCapture={handlePointerDownCapture}
            onPointerMoveCapture={handlePointerMoveCapture}
            onPointerUpCapture={handlePointerUpCapture}
            onPointerCancelCapture={resetTapIntent}
        >
            {/* Names the carousel after the discussion/series this media belongs to.
                A feed renders many of these, so a fixed localized string such as "Media" would leave a
                screen-reader user with a list of identical landmarks: the same defect, renamed. `alt`
                is already the title at all three call sites. `|| undefined` and not `|| ""`, because an
                empty aria-label is worse than none: it suppresses any fallback name.

                `role="group"`, not the primitive's `region` (2026-09-24, gate `b87b19f3b8`): a card's
                media is part of the card, not a page landmark. As a region, a feed or a landing showcase
                put one landmark per card into the screen-reader landmark list, and two cards sharing a
                title (two series both called "Gate Normal Series" on `/start-a-series`) made two
                identically named landmarks. A named group keeps the carousel semantics and the name. */}
            <Carousel
                setApi={setCarouselApi}
                opts={{ align: "center" }}
                className="mx-auto w-full"
                role="group"
                aria-label={alt || undefined}
            >
                <CarouselContent className="-ml-0 w-full">
                    {preparedItems.map((item, index) => {
                        const isBroken = resolvedSlideStatuses[index] === "error";
                        const providerName = providerDisplayName(item.provider);
                        // A clip rides the SAME click-to-activate mechanics as a provider embed, and
                        // that is the point rather than a shortcut: both show a poster and mount
                        // nothing heavy until the reader asks. For a clip that IS the preload="none"
                        // posture §7 is built around — one scrolling past a feed costs one WebP and
                        // no video bytes at all.
                        const isActivatable = item.kind === "iframe" || item.kind === "clip";
                        const iframeIsLoaded = isActivatable && loadedIframeIndexes.has(index);
                        const isPriorityImage = priorityFirstSlide && index === 0;

                        return (
                            <CarouselItem key={`discussion-card-media-${index}-${item.kind}-${item.sourceUrl}`} className={cn("pl-0", slideHeightClassName)}>
                                {/* SV-135 S8, D4: the letterbox surface is flat `--muted`. The blurred backdrop that used to
                                    sit here is gone; the media box clips the corners (`01-base.css`). */}
                                <div
                                    data-card-media-slide-surface=""
                                    className="relative flex h-full w-full items-center justify-center overflow-hidden bg-muted"
                                >
                                    {isActivatable ? (
                                        <div className="relative z-[1] h-full w-full">
                                            {iframeIsLoaded ? (
                                                item.kind === "clip" ? (
                                                    // The §7 player contract, identical to the in-body
                                                    // player: never autoplay; muted, which is what
                                                    // lets a gesture-triggered play() be allowed at
                                                    // all; playsinline so iOS does not go fullscreen;
                                                    // loop; and visible controls, the only way audio
                                                    // is ever unmuted — by the reader's own gesture.
                                                    <video
                                                        src={item.sourceUrl}
                                                        poster={item.previewUrl ?? undefined}
                                                        preload="none"
                                                        muted={true}
                                                        loop={true}
                                                        controls={true}
                                                        playsInline={true}
                                                        autoPlay={false}
                                                        className="h-full w-full object-contain"
                                                        data-playwright-id="card-clip-player-video"
                                                        data-ignore-carousel-tap="true"
                                                    />
                                                ) : (
                                                    <iframe
                                                        src={item.embedUrl ?? item.sourceUrl}
                                                        title={`${providerName} embed`}
                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                        allowFullScreen={true}
                                                        loading="eager"
                                                        referrerPolicy="strict-origin-when-cross-origin"
                                                        className="h-full w-full border-0"
                                                        data-ignore-carousel-tap="true"
                                                    />
                                                )
                                            ) : null}

                                            <button
                                                type="button"
                                                data-ignore-carousel-tap="true"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    markIframeAsLoaded(index);
                                                }}
                                                className={cn(
                                                    "absolute inset-0 z-[2] flex h-full w-full items-center justify-center overflow-hidden border-0 bg-transparent p-0 text-left transition-all duration-300 ease-out",
                                                    iframeIsLoaded
                                                        ? "pointer-events-none opacity-0 backdrop-blur-0"
                                                        : "opacity-100 backdrop-blur-[2px]",
                                                )}
                                                aria-label={localizer("discussion.card.media.play_here")}
                                                aria-hidden={iframeIsLoaded ? "true" : undefined}
                                            >
                                                {item.previewUrl ? (
                                                    <>
                                                        <SafeImage
                                                            src={item.previewUrl}
                                                            alt={localizer("discussion.card.media.video_preview")}
                                                            seavienImageSize={seavienImageSize}
                                                            mode="keep-layout"
                                                            wrapperClassName="relative z-[1] flex h-full w-full items-center justify-center"
                                                            className={cn(
                                                                "z-[1] max-h-full max-w-full object-contain transition-all duration-300",
                                                                iframeIsLoaded ? "scale-[1.02] opacity-0" : "opacity-100",
                                                            )}
                                                        />
                                                    </>
                                                ) : (
                                                    <div
                                                        className={cn(
                                                            "absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.08),transparent_55%),linear-gradient(135deg,rgba(20,20,22,0.92),rgba(36,36,44,0.96))] transition-opacity duration-300",
                                                            iframeIsLoaded ? "opacity-0" : "opacity-100",
                                                        )}
                                                        aria-hidden="true"
                                                    />
                                                )}

                                                <div className="pointer-events-none absolute inset-0 z-[3] flex items-center justify-center px-4 text-center">
                                                    <span className="inline-flex items-center justify-center gap-2 rounded-full border border-border/70 bg-background1/95 px-5 py-2.5 text-sm font-medium text-text-main shadow-md backdrop-blur-sm transition hover:bg-background2/95">
                                                        <Play className="size-4 fill-current" aria-hidden={true} />
                                                        <span>{localizer("discussion.card.media.play_here")}</span>
                                                    </span>
                                                </div>
                                            </button>
                                        </div>
                                    ) : !isBroken ? (
                                        <>
                                            {/* SV-135 S8, D4 (2026-09-23): the blurred backdrop that
                                                filled the letterbox is deleted, which also closes
                                                `HOME-FEED-3` (its light branch inverted the surround's
                                                colour) by removal. The foreground image is now the only
                                                picture painted, so it is the LCP element (SV-07). */}
                                            <SafeImage
                                                src={item.previewUrl}
                                                alt={alt}
                                                seavienImageSize={seavienImageSize}
                                                seavienSrcSetSizes={seavienSrcSetSizes}
                                                sizes={imageSizes}
                                                mode="keep-layout"
                                                priority={isPriorityImage}
                                                wrapperClassName="relative z-[1] flex h-full w-full items-center justify-center"
                                                className="z-[2] max-h-full max-w-full object-contain"
                                                placeholder={<BrokenImageCard localizer={localizer} />}
                                                onLoad={() => {
                                                    setSlideStatus(index, "loaded");
                                                }}
                                                onError={() => {
                                                    setSlideStatus(index, "error");
                                                }}
                                            />
                                        </>
                                    ) : (
                                        <BrokenImageCard localizer={localizer} />
                                    )}
                                </div>
                            </CarouselItem>
                        );
                    })}
                </CarouselContent>

                {preparedItems.length > 1 && (
                    <>
                        {/* `standard-hover`, not a colour flip. The design language allows exactly one
                            hover treatment — a single background wash — and forbids changing text or
                            border colour on hover, which `hover:text-primary` did on both arrows and on
                            the play pill below. */}
                        <CarouselNext aria-label={nextMediaLabel} className="standard-hover absolute right-2 top-1/2 z-[3] -translate-y-1/2 pointer-events-auto bg-background/90! text-text-main cursor-pointer" />
                        <CarouselPrevious aria-label={previousMediaLabel} className="standard-hover absolute left-2 top-1/2 z-[3] -translate-y-1/2 pointer-events-auto bg-background/90! text-text-main cursor-pointer" />
                    </>
                )}

                {preparedItems.length > 1 && (
                    // The gap grows on a coarse pointer because the HIT AREA does (below). At the old
                    // 6px gap the pitch was 14px, so four centred 44px expanders would have stacked
                    // on top of each other and the first three dots would have become untappable —
                    // a hit-area "fix" that removes hit area. 12px takes the pitch to 28px, which
                    // leaves a bounded overlap whose worst case is landing on the adjacent slide:
                    // read-only, visible, and undone by one more tap. Same ceiling the achievements
                    // tile grid accepts, and for the same reason.
                    <div className="absolute bottom-2 left-1/2 z-[3] flex -translate-x-1/2 gap-1.5 [@media(pointer:coarse)]:gap-3 rounded-full bg-background1 p-1 shadow-sm">
                        {preparedItems.map((_, index) => {
                            const isActive = activeIndex === index;
                            const isBroken = resolvedSlideStatuses[index] === "error";

                            return (
                                <button
                                    key={`discussion-card-media-bullet-${index}`}
                                    type="button"
                                    data-ignore-carousel-tap="true"
                                    aria-label={localizer("discussion.card.media.go_to", index + 1, preparedItems.length)}
                                    aria-current={isActive || undefined}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        carouselApi?.scrollTo(index);
                                    }}
                                    // `relative` is load-bearing: touch-hit-44 sets no `position` by
                                    // design, so a statically positioned call site anchors the expander
                                    // to some ancestor and silently gains nothing (pinned by
                                    // components/ui/tapTargets.contract.test.tsx).
                                    className="relative touch-hit-44 flex size-4 cursor-pointer items-center justify-center"
                                >
                                    {/* The glyph is an inner span so the hit box can grow without the
                                        dot growing with it — these sit over a photo, where a 44px filled
                                        circle would be a visual-design change, not a hit-area one. */}
                                    <span
                                        aria-hidden="true"
                                        className={cn(
                                            "block h-2 w-2 rounded-full transition-all duration-150",
                                            isActive
                                                ? isBroken
                                                    ? "bg-[var(--warning)] scale-110"
                                                    : "bg-primary scale-110"
                                                : isBroken
                                                    ? "bg-[var(--warning-border)] hover:bg-[var(--warning)]"
                                                    : "bg-text-main opacity-40 hover:opacity-100",
                                        )}
                                    />
                                </button>
                            );
                        })}
                    </div>
                )}
            </Carousel>
        </div>
    );
}
